/**
 * <s-parallax> — Transform-only scroll parallax.
 *
 * Each descendant with [data-depth] (a number, e.g. 0.15 or -0.1) is
 * translated on the block axis by depth × the element's distance from the
 * viewport centre. Only transform is written, so layout never shifts.
 * Disabled entirely when the visitor prefers reduced motion.
 */
class SParallax extends HTMLElement {
  #layers = [];
  #frame = 0;
  #visible = false;
  #controller = null;
  #observer = null;
  #motionQuery = null;

  connectedCallback() {
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#layers = Array.from(this.querySelectorAll('[data-depth]'));
    if (this.#layers.length === 0) return;

    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      if (this.#visible) this.#schedule();
    }, { rootMargin: '20% 0px' });
    this.#observer.observe(this);

    window.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    window.addEventListener('resize', () => this.#schedule(), { passive: true, signal });
    this.#motionQuery.addEventListener('change', () => this.#schedule(), { signal });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    if (this.#motionQuery.matches) {
      for (const layer of this.#layers) layer.style.transform = '';
      return;
    }
    if (!this.#visible) return;

    const rect = this.getBoundingClientRect();
    const offset = rect.top + rect.height / 2 - window.innerHeight / 2;
    // Smaller viewports get a gentler effect so stacked layouts never collide.
    const scale = window.innerWidth < 768 ? 0.5 : 1;

    for (const layer of this.#layers) {
      const depth = Number.parseFloat(layer.dataset.depth) || 0;
      layer.style.transform = `translate3d(0, ${(-offset * depth * scale).toFixed(1)}px, 0)`;
    }
  }
}

if (!customElements.get('s-parallax')) customElements.define('s-parallax', SParallax);
